/** Proposal data hook — TanStack Query wrappers for the /proposal/:id page.
 *
 * Centralises all server-state reads and mutations so presentation components
 * remain focused on rendering.
 */
import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { keys } from "@/lib/queryKeys";
import { STALE, GC } from "@/lib/queryClient";
import { invalidate } from "@/lib/queryInvalidate";

// ---- Reads ----
export function useProposal(id) {
  return useQuery({
    queryKey: keys.proposal(id),
    queryFn: async () => (await api.get(`/proposals/${id}`)).data,
    enabled: !!id,
    staleTime: STALE.SHORT,
    gcTime: GC.SHORT,
  });
}

export function useProposalVersions(id) {
  return useQuery({
    queryKey: keys.proposalVersions(id),
    queryFn: async () => (await api.get(`/proposals/${id}/versions`)).data,
    enabled: !!id,
    staleTime: STALE.SHORT,
    gcTime: GC.SHORT,
  });
}

export function usePortfolios() {
  return useQuery({
    queryKey: keys.portfolios(),
    queryFn: async () => {
      const r = await api.get("/portfolios");
      return r.data.portfolios || [];
    },
    staleTime: STALE.SHORT,
    gcTime: GC.SHORT,
  });
}

export function useBars(symbol, timeframe) {
  const limit = timeframe === "5m" || timeframe === "15m" ? 240
    : timeframe === "1h" || timeframe === "4h" ? 180 : 200;
  return useQuery({
    queryKey: keys.bars(symbol, timeframe, limit),
    queryFn: async () => (await api.get(`/market/bars/${symbol}`, { params: { timeframe, limit } })).data || [],
    enabled: !!symbol,
    staleTime: STALE.MEDIUM,
    gcTime: GC.MEDIUM,
  });
}

export function useSizing({ symbol, entry, stop, portfolioKey, proposalId }) {
  const e = parseFloat(entry), s = parseFloat(stop);
  const validNumeric = !!(e && s && s < e && symbol && portfolioKey);
  return useQuery({
    queryKey: keys.sizing({ symbol, entry: validNumeric ? e : null, stop: validNumeric ? s : null, portfolioKey, proposalId }),
    queryFn: async () => (await api.get("/risk/sizing", {
      params: { entry: e, stop: s, portfolio_key: portfolioKey, symbol },
    })).data,
    enabled: validNumeric,
    staleTime: STALE.REAL_TIME,
    gcTime: GC.SHORT,
  });
}

// ---- Mutations ----
export function useProposalMutations(id, symbol) {
  const qc = useQueryClient();
  return {
    rerun: useMutation({
      mutationFn: async () => (await api.post(`/proposals/${id}/rerun`)).data,
      onSuccess: (data) => {
        invalidate.onAnalysisRefreshed(symbol);
        qc.invalidateQueries({ queryKey: keys.proposalVersions(id) });
        return data;
      },
    }),
    applyRiskFix: useMutation({
      mutationFn: async ({ planId, action, value }) => (
        await api.post("/risk/apply-fix", { proposal_id: id, plan_id: planId, action, value })
      ).data,
      onSuccess: () => invalidate.onRiskFixApplied(id),
    }),
    runRiskCheck: useMutation({
      mutationFn: async (payload) => (await api.post("/risk/check", { proposal_id: id, ...payload })).data,
    }),
    reject: useMutation({
      mutationFn: async (reason) => (await api.post(`/proposals/${id}/reject`, { reason })).data,
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: keys.proposal(id) });
        qc.invalidateQueries({ queryKey: keys.opportunities() });
      },
    }),
  };
}
